import { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, Mail, MapPin, Phone, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    service: '',
    message: '',
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const services = [
    'AI Consulting',
    'AI Creation',
    'Media & Brand',
    'Website Development',
    'Video Production',
    'Social Media',
    'Other',
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact`, formData);
      if (response.data.success) {
        setStatus({
          type: 'success',
          message: 'Thank you! Your message has been sent. We\'ll be in touch soon.',
        });
        setFormData({
          name: '',
          email: '',
          company: '',
          service: '',
          message: '',
        });
      }
    } catch (error) {
      setStatus({
        type: 'error',
        message: 'Something went wrong. Please try again or email us directly.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Hero */}
      <section className="min-h-[50vh] flex items-center relative overflow-hidden bg-elate-surface">
        <div className="container-custom relative z-10 pt-32 pb-20">
          <motion.p
            className="caption text-elate-yellow mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Contact
          </motion.p>

          <motion.h1
            className="text-headline text-white mb-8"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Let's
            <br />
            <span className="text-elate-yellow">Talk</span>
          </motion.h1>

          <motion.p
            className="text-lg md:text-xl text-gray-400 max-w-2xl leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            Have a project in mind? We'd love to hear about it. Fill in the form below or reach out directly.
          </motion.p>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm uppercase tracking-wider text-gray-400 mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full bg-elate-surface border border-white/10 px-4 py-3 text-white focus:border-elate-yellow focus:outline-none transition-colors"
                      placeholder="Your name"
                      data-testid="contact-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm uppercase tracking-wider text-gray-400 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full bg-elate-surface border border-white/10 px-4 py-3 text-white focus:border-elate-yellow focus:outline-none transition-colors"
                      placeholder="your@email.com"
                      data-testid="contact-email"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm uppercase tracking-wider text-gray-400 mb-2">
                      Company
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full bg-elate-surface border border-white/10 px-4 py-3 text-white focus:border-elate-yellow focus:outline-none transition-colors"
                      placeholder="Your company"
                      data-testid="contact-company"
                    />
                  </div>
                  <div>
                    <label className="block text-sm uppercase tracking-wider text-gray-400 mb-2">
                      Service Interested In
                    </label>
                    <select
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      className="w-full bg-elate-surface border border-white/10 px-4 py-3 text-white focus:border-elate-yellow focus:outline-none transition-colors"
                      data-testid="contact-service"
                    >
                      <option value="">Select a service</option>
                      {services.map((service) => (
                        <option key={service} value={service}>
                          {service}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm uppercase tracking-wider text-gray-400 mb-2">
                    Message *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="w-full bg-elate-surface border border-white/10 px-4 py-3 text-white focus:border-elate-yellow focus:outline-none transition-colors resize-none"
                    placeholder="Tell us about your project..."
                    data-testid="contact-message"
                  />
                </div>

                {status.message && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`flex items-center gap-3 p-4 ${
                      status.type === 'success'
                        ? 'bg-green-500/10 text-green-400 border border-green-500/20'
                        : 'bg-red-500/10 text-red-400 border border-red-500/20'
                    }`}
                    data-testid="contact-status"
                  >
                    {status.type === 'success' ? (
                      <CheckCircle size={20} />
                    ) : (
                      <AlertCircle size={20} />
                    )}
                    {status.message}
                  </motion.div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="contact-submit"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                  <Send size={18} />
                </button>
              </form>
            </motion.div>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="bg-elate-surface p-8 md:p-12 h-full">
                <h3 className="text-2xl font-heading uppercase text-white mb-8">
                  Get in Touch
                </h3>

                <div className="space-y-8">
                  <div className="flex items-start gap-4">
                    <Mail className="w-6 h-6 text-elate-yellow flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-sm uppercase tracking-wider text-gray-400 mb-1">Email</p>
                      <a
                        href="mailto:info@elate.global"
                        className="text-white hover:text-elate-yellow transition-colors"
                      >
                        info@elate.global
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <MapPin className="w-6 h-6 text-elate-yellow flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-sm uppercase tracking-wider text-gray-400 mb-1">Location</p>
                      <p className="text-white">London, United Kingdom</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <Phone className="w-6 h-6 text-elate-yellow flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-sm uppercase tracking-wider text-gray-400 mb-1">Phone</p>
                      <p className="text-white">Available on request</p>
                    </div>
                  </div>
                </div>

                <hr className="border-white/10 my-8" />

                <div>
                  <p className="text-sm uppercase tracking-wider text-gray-400 mb-4">Social</p>
                  <div className="flex gap-4">
                    <a
                      href="https://x.com/elateglobal"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white hover:text-elate-yellow transition-colors uppercase tracking-wider text-sm font-semibold"
                    >
                      X
                    </a>
                    <a
                      href="https://instagram.com/elateglobal"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white hover:text-elate-yellow transition-colors uppercase tracking-wider text-sm font-semibold"
                    >
                      Instagram
                    </a>
                  </div>
                </div>

                <hr className="border-white/10 my-8" />

                <div className="bg-elate-yellow p-6">
                  <p className="text-elate-black font-semibold mb-2">
                    Prefer email?
                  </p>
                  <p className="text-elate-black/70 text-sm">
                    Drop us a line at{' '}
                    <a href="mailto:info@elate.global" className="underline">
                      info@elate.global
                    </a>{' '}
                    and we'll get back to you within 24 hours.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
